import { z } from 'zod/v4'

// The single source for each client (UI) tool: its Zod param schema, its kind
// (silent vs. confirm), and — for confirm tools — its confirmation card id + title.
// The model schema, runtime validation, client dispatch, and telemetry id all derive
// from this registry.

export enum ToolKind {
  Silent = 'silent', // executes immediately in the UI
  Confirm = 'confirm', // renders a confirmation card; runs only on accept
}

/**
 * WHEN the paused ADK session is resumed for a confirm tool. Declared here, next to the tool, because
 * it used to live as a hand-kept `OFFER_CONFIRM_TOOLS` Set inside a streaming composable — a second
 * place to remember, and the kind of list that silently omits a new tool.
 *
 * - `immediate`  — the agent's job was to PROPOSE. Resume with an ack as soon as the card renders;
 *   whatever the user then clicks runs its own flow and is not the tool's result.
 * - `on-decision` — the agent must learn what actually happened, so the session stays paused until
 *   the user accepts or declines. Every destructive tool is this.
 */
export type ResumeTiming = 'immediate' | 'on-decision'

type ToolSpec =
  | { kind: ToolKind.Silent; description: string; params: z.ZodObject<any> }
  | { kind: ToolKind.Confirm; description: string; params: z.ZodObject<any>; card: string; cardTitle: string; resume: ResumeTiming }

export const Tools = {
  // ── silent: the FILE EXPLORER (file/folder tree, useBrowser) ──
  filter_files: {
    kind: ToolKind.Silent,
    description: 'UI ACTION: Filters the file explorer list by text. Pass an empty query to clear the filter. Only works while the file explorer is actually on screen (see INTERFACE STATE for "The file explorer is open" each turn) — if it is not shown there, or if this call comes back with ok:false, say plainly that you cannot see a file explorer right now instead of claiming to have filtered. Never report having filtered without actually calling this tool and getting a result back.',
    params: z.object({ query: z.string() }),
  },
  select_files: {
    kind: ToolKind.Silent,
    description: 'UI ACTION: Selects/highlights entries in the file explorer by their full id (UUID). Only works while the file explorer is actually on screen (see INTERFACE STATE for "The file explorer is open" each turn) — if that line is not there, do NOT call this tool, even when you know the id: say plainly that you cannot see a file explorer right now and ask the user to open it. Never report having selected anything without actually calling this tool.',
    params: z.object({ document_ids: z.array(z.string()) }),
  },

  // ── silent: the currently OPEN SHEET (spreadsheet view) ──
  filter_sheet: {
    kind: ToolKind.Silent,
    description: 'UI ACTION: Filters the rows of the open sheet by text. Pass an empty query to clear the filter.',
    params: z.object({ query: z.string() }),
  },
  select_sheet_rows: {
    kind: ToolKind.Silent,
    description: 'UI ACTION: Selects rows of the open sheet by their document id (UUID).',
    params: z.object({ document_ids: z.array(z.string()) }),
  },

  // ── silent: global / document ──
  open_document: {
    kind: ToolKind.Silent,
    description: 'UI ACTION: Navigates the screen to the detail page of a document. Use it only when the user asks to open, view or go to a document. It does NOT return the data of the document and is not for reading or editing: to read the content or the extracted data use `documents` with its `document_id`.',
    params: z.object({ document_id: z.string() }),
  },
  navigate_to: {
    kind: ToolKind.Silent,
    description: 'UI ACTION: Navigates the user to any page of the application. To open a specific article of a law, use path="/app/laws/id/{norm_id}" with query={"article": "270"}. The norm_id is available in the results of query_legal_catalog (the [norm_id: ...] field). Routes: /app (home), /app/{projectSlug} (project), /app/laws (legislation), /app/laws/id/{norm_id} (a specific law), /app/settings, /app/settings/rules, /app/settings/templates, /app/settings/organization, /app/history, /app/workflows, /app/cases (cases), /app/clients (clients), /app/clients/{client_id}, /app/cases/{case_id} (case file). To OPEN a case or a client use `open_case_or_client`, not this tool: navigating alone leaves the conversation pointing at the previous scope.',
    params: z.object({ path: z.string(), query: z.record(z.string(), z.string()).optional() }),
  },
  highlight_text: {
    kind: ToolKind.Silent,
    description: 'UI ACTION: Highlights and scrolls to a fragment of text in the open document. Provide the exact text, or a distinctive phrase, so it can be found. Optionally give the page number and a short label.',
    params: z.object({ text: z.string(), page: z.number().optional(), label: z.string().optional() }),
  },

  // ── silent: the chat's document SCOPE — narrowing/moving between rungs (§6b) ──
  // The counterpart to `clear_chat_scope`: that one WIDENS (to global) and needs the user's
  // confirmation; this one moves to a specific case or client the user named, which is the thing
  // they just asked for. Both rungs stay reachable only through the store's single `setScope` path.
  open_case_or_client: {
    kind: ToolKind.Silent,
    description: 'UI ACTION: Opens a case (expediente) or a client — navigates the screen to it AND points this conversation at it, so your document tools then see that case/client instead of what they saw before. Use it when the user asks to open, go to, or switch to a case or client. The id must come from the ACTIVE SCOPE block or from `find_case_or_client`; never invent one.',
    params: z.object({
      kind: z.enum(['case', 'client']).describe('Which rung to open: a single case, or a client (all of its cases).'),
      id: z.string().describe('The full UUID of the case or client. Never shown to the user.'),
    }),
  },

  // ── confirm: the chat's document SCOPE (§6b) ──
  // Scope is STRICT: while a case or client is selected, nothing outside it is reachable — not by
  // search, not by id. The agent cannot widen the scope itself; it can only ASK, and the user
  // confirms in the UI.
  //
  // It is DECLARED UNCONDITIONALLY, including to an unscoped chat, and the description below is what
  // holds the line ("Use it ONLY when the user has asked for something that lies outside the active
  // scope"). This comment used to claim the tool was "offered only when a scope is actually set" —
  // that gate was a `ViewState`-keyed selector nothing has called since 2026-08-25 (#505). Restoring
  // it is a behaviour change and needs a wired call site, not a comment.
  clear_chat_scope: {
    kind: ToolKind.Confirm,
    card: 'clearScope',
    resume: 'on-decision',
    cardTitle: 'Quitar el ámbito',
    description: 'UI ACTION AND CONFIRMATION: Asks the user to drop the active scope (a case or a client) so that ALL of their documents become reachable. Use it ONLY when the user has asked for something that lies outside the active scope. Do NOT use it on your own initiative merely because documents outside the scope are attached: the user chose that scope deliberately. NEVER claim a document does not exist just because you cannot find it: while a scope is active, it may exist outside it. Explain in `reason` what the user needs and why the scope prevents it. It does not change the scope by itself: the user must confirm.',
    params: z.object({}),
  },

  // ── confirm: file operations on the user's / organization's document STORAGE ──
  // These mutate STORED files (not chat context). Each description says so to prevent
  // the agent confusing storage operations with context operations.
  delete_files: {
    kind: ToolKind.Confirm,
    card: 'delete',
    resume: 'on-decision',
    cardTitle: 'Confirmar eliminación',
    description: 'UI ACTION AND CONFIRMATION: Permanently deletes files/folders from the document storage of the user or the organization. It acts on STORED files by id — not on the chat context. Folders are deleted recursively. Include names so the confirmation reads clearly.',
    params: z.object({ items: z.array(z.object({ id: z.string(), name: z.string(), type: z.string().optional() })) }),
  },
  move_files: {
    kind: ToolKind.Confirm,
    card: 'move',
    resume: 'on-decision',
    cardTitle: 'Confirmar movimiento',
    description: 'UI ACTION AND CONFIRMATION: Moves files/folders into another EXISTING folder within the document storage of the user or the organization. Use it to organize files into folders. It cannot put a document in a case (expediente) — use `file_to_case` for that. Include names in items so the confirmation reads clearly.',
    params: z.object({ items: z.array(z.object({ id: z.string(), name: z.string(), type: z.string().optional() })), destination: z.string() }),
  },
  rename_files: {
    kind: ToolKind.Confirm,
    card: 'rename',
    resume: 'on-decision',
    cardTitle: 'Confirmar cambio de nombre',
    description: 'UI ACTION AND CONFIRMATION: Changes the display name of a file in the document storage of the user or the organization. It changes only the name of the stored file.',
    params: z.object({ file_id: z.string(), current_name: z.string(), new_name: z.string() }),
  },
  // Was the ungated `create_user_directory` SERVER tool, which wrote before any card — and was how
  // "move this to case X" turned into a stray root folder named X (MAR-988). A folder is a write the
  // user confirms like any other.
  create_folder: {
    kind: ToolKind.Confirm,
    card: 'createFolder',
    resume: 'on-decision',
    cardTitle: 'Confirmar nueva carpeta',
    description: 'UI ACTION AND CONFIRMATION: Asks the user to confirm creating a new folder in the document storage of the user or the organization. Nothing is created until they accept. Use it ONLY when the user asked for a folder. A folder is NOT a case (expediente): to put a document in a case, use `file_to_case`, never a folder named after the case.',
    params: z.object({
      name: z.string().describe('Name of the new folder.'),
      path: z.string().optional().describe('Parent folder it is created in, e.g. "/contracts". Defaults to "/".'),
    }),
  },

  // ── confirm: CASE membership (MAR-988) ──
  // Filing a document to a case LINKS it (a `case_documents` row, via `addCaseDocument`); the file
  // stays where it is in the library. A case's documents are exactly its links, so a folder named
  // after the case never shows up in it — which is the bug this tool exists to end.
  file_to_case: {
    kind: ToolKind.Confirm,
    card: 'fileToCase',
    resume: 'on-decision',
    cardTitle: 'Agregar al caso',
    description: 'UI ACTION AND CONFIRMATION: Adds existing documents to a case (expediente) so they appear among that case\'s documents. Use it whenever the user asks to put, file, add, attach or MOVE a document to a case. It links the documents to the case; it does not move or copy the files, which stay where they are in the library. `case_id` must come from `find_case_or_client` or the ACTIVE SCOPE block — never invent one. Never create a folder named after the case instead: a folder is not a case. Include names in items so the confirmation reads clearly.',
    params: z.object({
      items: z.array(z.object({ id: z.string(), name: z.string(), type: z.string().optional() })),
      case_id: z.string().describe('The full UUID of the case. Never shown to the user.'),
      case_name: z.string().describe('The case\'s name, as find_case_or_client returned it.'),
    }),
  },

  // ── confirm: present documents → add to chat context (THE single context tool) ──
  // Interactive pick-list: each row toggles a doc in/out of context; a single-result
  // call auto-adds it. Use it both to present search results AND to attach a specific
  // named set ("adjunta los dos contratos que mencionamos"). Replaced add_documents_to_context.
  present_document_results: {
    kind: ToolKind.Confirm,
    card: 'searchResults',
    resume: 'immediate',
    cardTitle: 'Documentos',
    description: 'UI ACTION: Presents the user with an interactive card of documents so they can add the ones they want to the chat context with one click (pass a single one and it is added automatically). Use it both to show search results (after `documents`, with the relevant set already filtered) AND to propose attaching a specific set. Do NOT list the documents as text: show them with this card. Pass `reason` as a descriptive heading. It does not read content and does not replace reading a document with `documents`.',
    params: z.object({ items: z.array(z.object({ id: z.string(), name: z.string(), type: z.string().optional(), contract_type: z.string().optional() })) }),
  },

  // ── confirm: download (offer card — buttons download directly, no accept/decline) ──
  download_documents: {
    kind: ToolKind.Confirm,
    card: 'download',
    resume: 'immediate',
    cardTitle: 'Descargar documentos',
    description: 'UI ACTION: Offers the user a card to download one or more already identified documents. The download happens when the user clicks; the agent downloads nothing by itself. Use it when the user asks to download an EXISTING document that has no other download affordance on screen. NEVER call this right after run_workflow finishes: that card already shows the produced document with its own download button, and a second card offering the identical file reads as a bug, not a second option.',
    params: z.object({ items: z.array(z.object({ id: z.string(), name: z.string(), type: z.string().optional() })) }),
  },

  // ── confirm: propose a workflow run, prefilled from the conversation (MAR-437) ──
  //
  // ONE STATIC TOOL, not one generated per workflow. The generated form (MAR-97) emitted a typed
  // tool per workflow from its `inputs[]`, which made a missing required input a tool-call error.
  // That is exactly what a PREFILL has to be allowed to do: the model supplies what the conversation
  // actually established and the human completes the rest on a real form, where every value is
  // visible and editable before anything runs. It also cannot survive the corpus — templates carry
  // 6 to 57 inputs, and a typed tool for each would put the whole catalogue's schemas on every turn.
  //
  // `values` is an ARRAY OF PAIRS rather than a record because a free-form record is not expressible
  // in the Gemini-accepted schema subset; the client coerces each value against the input's declared
  // type, which it reads from the workflow itself rather than trusting the model.
  run_workflow: {
    kind: ToolKind.Confirm,
    card: 'workflowRun',
    // The agent's job ends at PROPOSING. Whatever the user then does is its own flow, and the run
    // outcome arrives later as its own turn — so this resumes as soon as the card renders. Waiting
    // would also hold the conversation's one paused task (`conversations.open_task_id`) open for the
    // entire length of the run.
    resume: 'immediate',
    cardTitle: 'Ejecutar automatización',
    description: 'UI ACTION: Proposes running a document workflow, prefilled with everything the conversation has already established. The user reviews, completes anything missing, and confirms — nothing runs until they do, so an incomplete prefill is fine and is better than a guessed value. `workflow_id` MUST come from a `search_workflows` result in this conversation; never invent one or reuse one from memory. When the workflow declares `entities` slots and inputs with `bind` (`<slot>.<property>`), pass known parties/entities in `entities` (`{ slot, entity_id, name }`) so the UI auto-fills every bound `<slot>.<property>` field from the entity\'s Ficha at once, and use `values` only for scalar facts or explicit overrides. Fill `values` only with facts the user actually gave you: leave a field out rather than inventing a name, a date or an amount. If several workflows could fit, ask the user which one instead of proposing the first. IF THE USER IS EDITING A PROPOSAL ALREADY ON SCREEN FOR THIS SAME WORKFLOW (e.g. "change the gender to male"), THIS IS A PATCH, NOT A RESTATEMENT: send ONLY the field(s) actually changing. The UI keeps every other value exactly as shown — including ones you never set yourself, such as a party the user picked from a list — so omitting them is what preserves the rest of the form. Resending an old value is harmless; guessing one you are not sure of, to avoid leaving it out, is not.',
    params: z.object({
      workflow_id: z.string().describe('The `id` from a search_workflows result. Never invented.'),
      entities: z.array(z.object({
        slot: z.string().describe('The entity `slot` name from the workflow\'s `entities` list (e.g. "trabajador", "patrono", "inmueble").'),
        entity_id: z.string().optional().describe('The known entity or party UUID when available in context.'),
        name: z.string().optional().describe('The entity\'s name as stated by the user or document, used to match a scoped entity candidate when `entity_id` is not known.'),
      })).optional().describe('Entity slot assignments. When an entity from the active case/client or conversation fills a slot declared in `entities`, pass `{ slot, entity_id, name }` here so the UI auto-fills every `<slot>.<property>` input from that entity\'s Ficha at once.'),
      values: z.array(z.object({
        key: z.string().describe('The input `key` exactly as search_workflows reported it.'),
        value: z.string().describe('The value, as text. Dates as YYYY-MM-DD; numbers unformatted.'),
      })).describe('The inputs you are setting or changing right now. Omit anything unknown AND anything already correct on an existing proposal — omitted fields are preserved, not cleared.'),
    }),
  },

  // ── confirm: FILE CONVERSION (the `tools` skill) ──
  //
  // NOT `run_workflow` with a converter's id. There is no converter id to pass any more: MAR-487
  // made the two converters code-only TOOLS (`shared/tools/tool.ts`'s `ToolId` union,
  // `server/tools/registry.ts`, executed into `tool_runs`), so `tool_id` below is a member of a
  // fixed union rather than a `workflow_definitions` uuid. The `conversion` skill that used to
  // reveal this card went with them; the `tools` skill names the two ids instead.
  //
  // The split is older than that change and survives it on its own reasoning. `run_workflow`'s card
  // exists to negotiate a FORM — it resolves the definition's real inputs, coerces values, seeds from
  // the prior proposal, and materializes a draft document on every edit so the preview stays live.
  // Every one of those is wrong for a conversion: it takes one document and nothing else, there is
  // nothing to negotiate, and eager materialization would fire a 30s vision call (90s for PDF) on
  // PROPOSAL and re-pay it on each edit. Starting a job and filling in a form are different
  // primitives; this is the first one (MAR-476, MAR-487).
  convert_document: {
    kind: ToolKind.Confirm,
    card: 'convertDocument',
    // Same reasoning as `run_workflow`: the agent's job ends at proposing. The conversion runs on the
    // queue and reports back as its own turn, so holding the conversation's one paused task open for
    // the length of it would buy nothing.
    resume: 'immediate',
    cardTitle: 'Convertir a Word',
    description: 'UI ACTION: Offers the user a card to convert one document they already have — a photo, a scan, or a PDF — into an editable Word document. Nothing runs until they confirm. `tool_id` is one of the two ids named in the `tools` skill, and `document_id` must be a document that actually exists: never invent either. MATCH THE TOOL TO THE FILE — `image_to_word` reads only JPG and PNG, `pdf_to_word` reads only PDF, and they are not interchangeable; if the file is neither kind, no tool applies and you should say so instead of proposing the closest one. The conversion produces a NEW document and never alters the original.',
    params: z.object({
      tool_id: z.enum(['image_to_word', 'pdf_to_word']).describe('Which tool converts this file: `image_to_word` for JPG/PNG, `pdf_to_word` for PDF.'),
      document_id: z.string().describe('UUID of the image or PDF to convert.'),
    }),
  },

} as const satisfies Record<string, ToolSpec>

export type ToolId = keyof typeof Tools
